import {uuid} from './random'

const _scriptCbCache: Record<string, Promise<any>> = Object.create(null)

/**
 * Helps load Google APIs asynchronously.
 *
 * @param {string} source
 * @param {string} callbackParam
 * @param {string=} globalName
 * @returns {Promise}
 */
export function loadScriptCallback<T>(source: string, callbackParam: string, globalName: string): Promise<T> {
    const key = JSON.stringify([source, callbackParam, globalName])

    if(!_scriptCbCache[key]) {
        _scriptCbCache[key] = new Promise((resolve, reject) => {
            const callbackFunc = uuid()
            const sep = source.includes('?') ? '&' : '?'
            const fullUrl = `${source}${sep}${encodeURIComponent(callbackParam)}=${encodeURIComponent(callbackFunc)}`;

            (window as AnyObject)[callbackFunc] = () => {
                if(Object.prototype.hasOwnProperty.call(window, globalName)) {
                    resolve((window as AnyObject)[globalName])
                } else {
                    reject(new Error(`"${globalName}" was not defined by "${fullUrl}"`))
                }
                delete (window as AnyObject)[callbackFunc]
            }

            loadScript(fullUrl, globalName).catch(reject)
        })
    }

    return _scriptCbCache[key]
}

export async function loadGoogleMapsApi(apiKey = env.GOOGLE_MAPS_API_KEY): Promise<typeof google.maps> {
    const googleApi = await loadScriptCallback<{
        maps: typeof google.maps
    }>(`https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places,drawing&loading=async`, 'callback', 'google')
    return googleApi.maps
}

const _scriptCache = new Map()

/**
 * Load an external script.
 *
 * @param url Absolute URL of script to load
 * @param name Name of global variable that the script is expected to define
 */
export function loadScript<T = any>(url: string, name: string): Promise<T> {
    let promise

    if(_scriptCache.has(url)) {
        // TODO: normalize URL
        promise = _scriptCache.get(url)
    } else {
        promise = new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.onerror = event => reject(new Error(`Failed to load '${url}'`))
            script.onload = resolve
            script.async = true
            script.src = url

            if(document.currentScript?.parentNode) {
                document.currentScript.parentNode.insertBefore(script, document.currentScript)
            } else {
                (document.head || document.getElementsByTagName('head')[0]).appendChild(script)
            }
        })

        _scriptCache.set(url, promise)
    }

    return promise.then(() => {
        if((globalThis as AnyObject)[name]) {
            return (globalThis as AnyObject)[name]
        } else {
            const errorMessage = `"${name}" was not created by "${url}"`
            if(__DEV__) {
                console.error(errorMessage)
            }
            throw new Error(errorMessage)
        }
    })
}
